import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useLanguage, tr } from "../i18n";
import { lp } from "../components/Navbar";
import { PageHero, CTABand } from "./Shared";
import { Reveal, Stagger, StaggerItem, SectionHead } from "../components/motion";
const POSITIONING = [{
  title: "Free",
  copy: "The ERI requires no payment and carries no obligation."
}, {
  title: "Fast",
  copy: "A structured questionnaire that respects your time."
}, {
  title: "Accessible",
  copy: "No technical preparation needed. Answer what you know."
}, {
  title: "Non-binding",
  copy: "An initial picture, not a commitment to certification."
}, {
  title: "An initial assessment",
  copy: "An indicative readiness score across key areas."
}, {
  title: "A gateway",
  copy: "The natural starting point for deeper ESAIA assessment."
}];
const INCLUDES = ["Ethical readiness assessment", "Initial AI governance overview", "Identification of key risk areas", "Readiness score", "Initial improvement areas"];
export default function ERI() {
  const {
    lang
  } = useLanguage();
  return <div data-testid="eri-page">
      <PageHero kicker={tr("Ethical Readiness Index")} title={tr("How ready is your organization for responsible AI?")} copy={tr("The Ethical Readiness Index (ERI) is ESAIA's introductory AI readiness assessment. It provides organizations with a structured starting point for understanding their current approach to responsible AI.")}>
        <Reveal delay={0.35} className="mt-8 flex flex-wrap items-center gap-4">
          <Link to={lp(lang, "/eri/assessment")} data-testid="eri-start-button" className="group inline-flex items-center gap-2 border border-esaia-blue bg-esaia-blue px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-esaia-blue">{tr("Start the Free ERI")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <SectionHead kicker={tr("What the ERI is")} title={tr("A structured starting point.")} copy={tr("The ERI gives you an initial score and an overview of the key areas that require attention. It is designed to be the first step in a longer assurance journey, not the last word on your AI governance.")} />
        <Stagger className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {POSITIONING.map((p, i) => <StaggerItem key={p.title}>
              <div className="h-full border border-esaia-line bg-white p-7 transition-all duration-300 hover:border-esaia-blue/50 hover:shadow-md" data-testid={`eri-positioning-${i}`}>
                <span className="font-serif text-3xl font-light text-esaia-blue/40">0{i + 1}</span>
                <h3 className="mt-4 text-lg font-semibold text-esaia-ink">{tr(p.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-esaia-muted">{tr(p.copy)}</p>
              </div>
            </StaggerItem>)}
        </Stagger>
      </section>

      <section className="border-y border-esaia-line bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <SectionHead kicker={tr("What you receive")} title={tr("Your readiness, made visible.")} copy={tr("After completing the ERI, you receive an initial readiness score with an overview of key areas requiring attention across governance, risk, oversight and responsible practice.")} />
            <Reveal delay={0.2} className="mt-8">
              <Link to={lp(lang, "/eri/assessment")} data-testid="eri-start-button-2" className="group inline-flex items-center gap-2 border border-esaia-blue bg-esaia-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-esaia-blue">{tr("Start the Free ERI")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="border border-esaia-line bg-esaia-canvas p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("The ERI includes")}</p>
                <ul className="mt-5 space-y-3">
                  {INCLUDES.map(inc => <li key={inc} className="flex items-start gap-3 text-sm text-esaia-ink">
                      <Check size={16} className="mt-0.5 shrink-0 text-esaia-blue" />
                      {tr(inc)}
                    </li>)}
                </ul>
                <div className="mt-8 border-t border-esaia-line pt-6">
                  <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">
                    <span>{tr("Readiness scale")}</span>
                    <span>{tr("0 to 100")}</span>
                  </div>
                  <div className="mt-3 flex h-2 w-full gap-1" aria-hidden="true">
                    {[0.25, 0.45, 0.65, 0.85, 1].map(o => <span key={o} className="flex-1 bg-esaia-blue" style={{
                    opacity: o * 0.9
                  }} />)}
                  </div>
                  <div className="mt-2 flex justify-between text-[10px] text-esaia-subtle">
                    <span>{tr("Attention required")}</span>
                    <span>{tr("Developing")}</span>
                    <span>{tr("Established")}</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand title={tr("Ready for more than an initial picture?")} copy={tr("The ERI is the gateway to ESAIA certification: a full independent assessment of your AI environment with a 12-month certificate and annual reassessment.")} primary={{
      to: "/certification",
      label: tr("Explore ESAIA Certification")
    }} secondary={{
      to: "/contact",
      label: tr("Request a Quote")
    }} />

    </div>;
}
